import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Briefcase, TrendingUp, Receipt, LogOut, ChevronDown, Shield, Settings, User } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';

const Layout = ({ user, onLogout, children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [portfolioOpen, setPortfolioOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const dropdownRef = useRef(null);
  const userMenuRef = useRef(null);

  // Get user from localStorage if not passed as prop
  const getUserFromStorage = () => {
  try {
    const storedUser = localStorage.getItem('user');
    if (!storedUser || storedUser === 'undefined' || storedUser === 'null') {
      localStorage.removeItem('user'); 
      return {};
    }
    return JSON.parse(storedUser);
  } catch (error) {
    console.error('Error parsing user from localStorage:', error);
    localStorage.removeItem('user'); // remove corrupted data
    return {};
  }
};

  const currentUser = user || getUserFromStorage();

  // Default logout handler if not provided
  const handleLogout = onLogout || (() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  });

  const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
    { name: 'Portfolio', href: '/dashboard/portfolio/performance', icon: Briefcase },
    { name: 'Transactions', href: '/dashboard/transactions', icon: Receipt },
  ];

  const portfolioMenu = [
    { name: 'Investments', href: '/dashboard/portfolio/investments' },
    { name: 'Offering Performance', href: '/dashboard/portfolio/performance' },
    { name: 'Criteria Set', href: '/dashboard/portfolio/criteria' },
  ];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setPortfolioOpen(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isPortfolioActive = location.pathname.includes('/portfolio');

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex flex-col items-center">
              <img
                src="https://customer-assets.emergentagent.com/job_fintech-portal-38/artifacts/7o5rddiv_veriquant_logo%20%281%29.webp"
                alt="Veriquant"
                className="h-[64px] w-auto cursor-pointer"
                style={{ imageRendering: 'crisp-edges', WebkitFontSmoothing: 'antialiased' }}
                onClick={() => navigate('/dashboard')}
              />
              <p className="text-xs text-slate-600 font-medium tracking-wide mt-1.5 text-center">
                Merchant Cash Advances: Simple. Fast. Flexible
              </p>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-3">
              {navigation.map((item) => {
                const Icon = item.icon;
                const isActive = item.name === 'Dashboard' 
                  ? location.pathname === '/dashboard'
                  : item.name === 'Portfolio'
                  ? location.pathname.includes('/portfolio')
                  : item.name === 'Transactions'
                  ? location.pathname.includes('/transactions')
                  : location.pathname === item.href;
                
                return (
                  <NavLink
                    key={item.name}
                    to={item.href}
                    className={`flex items-center space-x-3 px-8 py-4 rounded-lg text-lg font-semibold transition-colors ${
                      isActive
                        ? 'bg-blue-50 text-blue-700'
                        : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                    }`}
                  >
                    <Icon className="w-6 h-6" />
                    <span>{item.name}</span>
                  </NavLink>
                );
              })}
            </nav>

            <div className="flex items-center space-x-4">
              {/* User Menu Dropdown */}
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center space-x-2 px-4 py-2 rounded-lg text-slate-700 hover:bg-slate-100 transition-colors"
                >
                  <User className="w-5 h-5" />
                  <span className="hidden md:inline font-medium">{currentUser?.first_name} {currentUser?.last_name}</span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {userMenuOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-slate-200 py-2 z-50">
                    <div className="px-4 py-2 border-b border-slate-200">
                      <p className="text-sm font-medium text-slate-900">{currentUser?.first_name} {currentUser?.last_name}</p>
                      <p className="text-xs text-slate-500">{currentUser?.email}</p>
                    </div>
                    <button
                      onClick={() => {
                        navigate('/dashboard/settings');
                        setUserMenuOpen(false);
                      }}
                      className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 transition-colors"
                    >
                      <Settings className="w-4 h-4" />
                      <span>Profile Settings</span>
                    </button>
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                    >
                      <LogOut className="w-4 h-4" />
                      <span>Logout</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Mobile Navigation */}
          <nav className="md:hidden flex items-center space-x-3 mt-4 overflow-x-auto pb-2">
            {navigation.map((item) => {
              const Icon = item.icon;
              const isActive = item.name === 'Dashboard' 
                ? location.pathname === '/dashboard'
                : item.name === 'Portfolio'
                ? location.pathname.includes('/portfolio')
                : item.name === 'Transactions'
                ? location.pathname.includes('/transactions')
                : location.pathname === item.href;
              
              return (
                <NavLink
                  key={item.name}
                  to={item.href}
                  className={`flex items-center space-x-3 px-6 py-3 rounded-lg text-lg font-semibold whitespace-nowrap transition-colors ${
                    isActive
                      ? 'bg-blue-50 text-blue-700'
                      : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  <Icon className="w-6 h-6" />
                  <span>{item.name}</span>
                </NavLink>
              );
            })}
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <main className="mx-auto px-6 py-8">
        {children}
      </main>
    </div>
  );
};

export default Layout;